import { useState, useCallback, useEffect } from 'react';

// Tournament State Machine States
export enum TournamentState {
  LOBBY = 'LOBBY',
  WAITING_FOR_ROUND = 'WAITING_FOR_ROUND',
  IN_GAME = 'IN_GAME',
  GAME_OVER = 'GAME_OVER',
  BYE_ROUND = 'BYE_ROUND',
  TOURNAMENT_OVER = 'TOURNAMENT_OVER',
  TOURNAMENT_CANCELLED = 'TOURNAMENT_CANCELLED',
}

// Valid state transitions
const VALID_TRANSITIONS: Record<TournamentState, TournamentState[]> = {
  [TournamentState.LOBBY]: [
    TournamentState.WAITING_FOR_ROUND,
    TournamentState.TOURNAMENT_CANCELLED,
  ],
  [TournamentState.WAITING_FOR_ROUND]: [
    TournamentState.IN_GAME,
    TournamentState.BYE_ROUND,
    TournamentState.TOURNAMENT_OVER,
    TournamentState.TOURNAMENT_CANCELLED,
  ],
  [TournamentState.IN_GAME]: [
    TournamentState.GAME_OVER,
    TournamentState.TOURNAMENT_CANCELLED,
  ],
  [TournamentState.GAME_OVER]: [
    TournamentState.WAITING_FOR_ROUND,
    TournamentState.TOURNAMENT_OVER,
    TournamentState.TOURNAMENT_CANCELLED,
  ],
  [TournamentState.BYE_ROUND]: [
    TournamentState.WAITING_FOR_ROUND,
    TournamentState.TOURNAMENT_OVER,
    TournamentState.TOURNAMENT_CANCELLED,
  ],
  [TournamentState.TOURNAMENT_OVER]: [],
  [TournamentState.TOURNAMENT_CANCELLED]: [],
};

interface TournamentStateMachineResult {
  state: TournamentState;
  canTransitionTo: (newState: TournamentState) => boolean;
  transitionTo: (newState: TournamentState) => void;
  isInLobby: boolean;
  isWaitingForRound: boolean;
  isInGame: boolean;
  isGameOver: boolean;
  isByeRound: boolean;
  isTournamentOver: boolean;
  isTournamentCancelled: boolean;
  isTournamentActive: boolean;
  canPlayGame: boolean;
}

export function useTournamentStateMachine(initialState: TournamentState = TournamentState.LOBBY): TournamentStateMachineResult {
  const [state, setState] = useState<TournamentState>(initialState);

  const canTransitionTo = useCallback((newState: TournamentState): boolean => {
    const validNextStates = VALID_TRANSITIONS[state];
    return validNextStates.includes(newState);
  }, [state]);

  const transitionTo = useCallback((newState: TournamentState) => {
    if (state === newState) {
      return;
    }

    if (!canTransitionTo(newState)) {
      console.error(
        `[TournamentStateMachine] Invalid state transition: ${state} → ${newState}`,
        `Valid transitions from ${state}:`,
        VALID_TRANSITIONS[state]
      );
      return;
    }

    setState(newState);
  }, [state, canTransitionTo]);

  // Computed state checks
  const isInLobby = state === TournamentState.LOBBY;
  const isWaitingForRound = state === TournamentState.WAITING_FOR_ROUND;
  const isInGame = state === TournamentState.IN_GAME;
  const isGameOver = state === TournamentState.GAME_OVER;
  const isByeRound = state === TournamentState.BYE_ROUND;
  const isTournamentOver = state === TournamentState.TOURNAMENT_OVER;
  const isTournamentCancelled = state === TournamentState.TOURNAMENT_CANCELLED;

  // Composite state checks
  const isTournamentActive = !(isTournamentOver || isTournamentCancelled);
  const canPlayGame = isInGame;

  useEffect(() => {
  }, [state]);

  return {
    state,
    canTransitionTo,
    transitionTo,
    isInLobby,
    isWaitingForRound,
    isInGame,
    isGameOver,
    isByeRound,
    isTournamentOver,
    isTournamentCancelled,
    isTournamentActive,
    canPlayGame,
  };
}
